import { useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pageImages } from '../../data/siteData';
import PagePhotoViewer from '../../elements/PagePhotoViewer';

const images = pageImages['conveyors-bale-conveying-bagging-systems'];

function ConveyorsBaleConveyingBaggingSystems() {
  const conveyorCarouselRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [activeConveyorSlide, setActiveConveyorSlide] = useState(0);

  const updateConveyorSlide = () => {
    const track = conveyorCarouselRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.cart-option-card'));
    const maxScroll = track.scrollWidth - track.clientWidth;
    const slideTargets = cards.map((card) => Math.min(card.offsetLeft - track.offsetLeft, maxScroll));

    if (track.scrollLeft >= maxScroll - 4) {
      setActiveConveyorSlide(cards.length - 1);
      return;
    }

    let currentIndex = 0;
    for (let index = 1; index < slideTargets.length; index += 1) {
      const threshold = (slideTargets[index - 1] + slideTargets[index]) / 2;
      if (track.scrollLeft >= threshold) currentIndex = index;
    }

    setActiveConveyorSlide(currentIndex);
  };

  const moveConveyorCarousel = (direction) => {
    const track = conveyorCarouselRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll('.cart-option-card'));
    const nextIndex = Math.min(Math.max(activeConveyorSlide + direction, 0), cards.length - 1);
    const nextCard = cards[nextIndex];
    const maxScroll = track.scrollWidth - track.clientWidth;

    track.scrollTo({
      left: Math.min(nextCard.offsetLeft - track.offsetLeft, maxScroll),
      behavior: 'smooth',
    });
    setActiveConveyorSlide(nextIndex);
  };

  const startConveyorDrag = (event) => {
    const track = conveyorCarouselRef.current;
    if (!track) return;
    track.setPointerCapture?.(event.pointerId);
    dragState.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: track.scrollLeft,
    };
    track.classList.add('dragging');
  };

  const moveConveyorDrag = (event) => {
    const track = conveyorCarouselRef.current;
    if (!track || !dragState.current.active) return;
    event.preventDefault();
    track.scrollLeft = dragState.current.scrollLeft - (event.clientX - dragState.current.startX);
  };

  const stopConveyorDrag = (event) => {
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragState.current.active = false;
    conveyorCarouselRef.current?.classList.remove('dragging');
    window.setTimeout(updateConveyorSlide, 120);
  };

  return (
    <main className="detail-main">
      <section className="detail-layout">
        <aside>
          <span className="eyebrow">In This Section</span>
          <Link to="/closed-end-manual-tie-balers">Closed-End Manual Tie Balers</Link>
          <Link to="/open-end-automatic-tie-balers">Open-End Automatic Tie Balers</Link>
          <Link to="/wide-mouth-open-end-automatic-tie-balers">Wide-Mouth Open-End Automatic Tie Balers</Link>
          <Link to="/multi-materials-full-eject-closed-end-balers">Multi Materials Full-Eject Closed-End Balers</Link>
          <Link to="/open-end-manual-tie-balers">Open-End Manual Tie Balers</Link>
          <Link to="/cart-lifters-dumpers-dump-carts">Cart Lifters, Dumpers &amp; Dump Carts</Link>
          <Link className="active" to="/conveyors-bale-conveying-bagging-systems">Conveyors, Bale Conveying &amp; Bagging Systems</Link>
          <Link to="/used-clothing-balers">Used Clothing Balers</Link>
          <Link to="/cotton-gin-textile-baling-presses">Cotton Gin &amp; Textile Baling Presses</Link>
          <Link to="/biomass-cotton-gin-trash-baling-press">Biomass &amp; Cotton Gin Trash Baling Press</Link>
          <Link to="/vertical-down-packing-baling-presses-balers">Vertical &amp; Down-Packing Baling Presses &amp; Balers</Link>
          <Link to="/custom-designed-specialty-balers">Custom Designed &amp; Specialty Balers</Link>
          <Link to="/precrush-compactors-waste-compactors">PreCrush Compactors &amp; Waste Compactors</Link>
        </aside>
        <article className="article">
          <span className="eyebrow">Products</span>
          <h2>Conveyors, Bale Conveying &amp; Bagging Systems</h2>
          <div className="body-copy page-summary-copy">Conveyors increase the efficiency of <strong>material handling</strong>. <strong>C+M Baling Systems</strong> offers many different options on a range of conveyors including hinged steel belt conveyors, sliderbed belt conveyors, powered roller conveyors, gravity roller conveyors and specialty conveyors. The conveyor(s) are designed for the sizes, types, and volume of material being conveyed into the C+M baler. The conveyors are incorporated into the C+M operator control system and integrated into the hydraulic system. C+M will engineer the baler/conveyor system to keep up with your required production.</div>
          <div className="page-summary-notes">
              <span>Baler loading conveyors</span>
              <span>Bale take-away systems</span>
              <span>Custom bagging and downstream handling</span>
          </div>
          <PagePhotoViewer title="Conveyors, Bale Conveying &amp; Bagging Systems" images={images} leadText={"Hinged steel belt pit conveyor with cleats, feeding tissue broke; tissue donuts, tissue logs, slab waste and trims into a C+M Open‐End, Double Tension Automatic Tie Baling Press. The pit section of this conveyor is 10′ long and sloped below floor level to make loading by dump cart, forklift or by hand. This design also helps make daily housekeeping simple to perform."} eyebrow="Equipment Photos" showLeadCopy={false} />
          <div className="content-stack">
            <section className="content-block">
              <span className="eyebrow">Equipment Photos</span>
              <h3>Conveyors, Bale Conveying &amp; Bagging Systems</h3>
              <div className="body-copy">Hinged steel belt pit conveyor with cleats, feeding tissue broke; tissue donuts, tissue logs, slab waste and trims into a C+M Open-End, Double Tension Automatic Tie Baling Press. The pit section of this conveyor is 10′ long and sloped below floor level to make loading by dump cart, forklift or by hand. This design also helps make daily housekeeping simple to perform.</div>
            </section>
            <section className="cart-carousel-section">
              <div className="cart-carousel-head">
                <div>
                  <span className="eyebrow">Conveyor Examples</span>
                  <h3>Conveyor Applications</h3>
                  <div className="body-copy">Sliderbed belt conveyors, cleated infeed conveyors, bale conveyors, and bagging systems can be arranged around the <strong>material stream</strong>, <strong>loading method</strong>, and downstream handling requirements.</div>
                </div>
              </div>
              <div
                className="cart-carousel"
                ref={conveyorCarouselRef}
                aria-label="Conveyor and bagging system examples"
                onPointerDown={startConveyorDrag}
                onPointerMove={moveConveyorDrag}
                onPointerUp={stopConveyorDrag}
                onPointerCancel={stopConveyorDrag}
                onScroll={updateConveyorSlide}
                style={{ '--carousel-end-space': 'calc(100% - min(410px, 82vw))' }}
              >
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/conveyors-material-handling/conveyor-2.jpg" alt="Inclined conveyor with floor-level loading feeding a C+M baler" draggable="false" />
                  </div>
                  <figcaption>Inclined conveyor with a floor-level loading area feeding a C+M baling press. Conveyor loading arrangements can be configured around the facility's collection and material handling workflow.</figcaption>
                </figure>
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/conveyors-material-handling/rubber-belt-2.jpg" alt="Blue rubber belt conveyor with raised side rails" draggable="false" />
                  </div>
                  <figcaption>Rubber belt conveyor with raised side rails to help contain loose material and reduce spillage during transfer to a baler.</figcaption>
                </figure>
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/conveyors-material-handling/belt-cleats.jpg" alt="Close-up of a conveyor belt with material lifting cleats" draggable="false" />
                  </div>
                  <figcaption>Conveyor belt cleats help carry material up an incline toward the baler infeed. Belt and cleat arrangements are selected for the material being handled.</figcaption>
                </figure>
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/horizontal-balers/horizontal-5.jpg" alt="Sliderbed belt conveyor with cleats feeding used clothing into a C+M baler" draggable="false" />
                  </div>
                  <figcaption>Sliderbed belt conveyor with cleats feeding used clothing into a C+M Open-End, Automatic Tie Baling Press, and is first unloaded by a C+M custom cart/lifter dumping system. The used clothes then inspected and metered onto the conveyor via a C+M material sorting table.</figcaption>
                </figure>
                <figure className="cart-option-card conveyor-option-card">
                  <div className="cart-option-image conveyor-option-image">
                    <img loading="lazy" decoding="async" src="/img/equipment/horizontal-balers/horizontal-8.jpg" alt="Sliderbed belt conveyor loaded with shredded broke and slab waste" draggable="false" />
                  </div>
                  <figcaption>Sliderbed belt conveyor being loaded with shredded broke and slab waste while being fed tissue trim by an airsystem all unloading into a C+M Open-End Automatic Tie Baling Press. The conveyor has very high side rails to contain the shredded material and avoid spillage. The belt is also cleated to assist in moving materials up the belt and into the baling press.</figcaption>
                </figure>
              </div>
              <div className="cart-carousel-controls" aria-label="Conveyor carousel controls">
                <button type="button" onClick={() => moveConveyorCarousel(-1)} aria-label="Show previous conveyor examples">
                  <ChevronLeft size={22} />
                </button>
                <div className="carousel-dots" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <span key={index} className={index === activeConveyorSlide ? 'active' : ''}></span>
                  ))}
                </div>
                <button type="button" onClick={() => moveConveyorCarousel(1)} aria-label="Show next conveyor examples">
                  <ChevronRight size={22} />
                </button>
              </div>
            </section>
          </div>
          <Link className="button" to="/contact/request-quote">Request a Quote <ArrowRight size={16} /></Link>
        </article>
      </section>
    </main>
  );
}

export default ConveyorsBaleConveyingBaggingSystems;
